import styled from 'styled-components';
import { useState } from 'react';
import { EventModal } from './EventModal';

// Warning - these are hardcoded!
const MIN_PER_HOUR = 60;
const FIRST_MIN = 9 * MIN_PER_HOUR; // 9 AM
const LAST_MIN = 17 * MIN_PER_HOUR; // 5 PM
const MINUTES_IN_DAY = LAST_MIN - FIRST_MIN;
const HEADER_HEIGHT = 31; // Height of the header in pixels
const CALENDAR_HEIGHT = 600 - HEADER_HEIGHT; // Height of the calendar in pixels (minus header)

const EVENT_COLORS = {
    assn: '#edf7f6',
    lecture: '#faebd7',
    break: '#fffbeb',
    handout: 'pink'
}

// Return the position and height of the calendar event
const getEventPosition = event => {
    let eventStart = event.start;
    if (eventStart < 5) {
        eventStart = 12 + eventStart; // Convert to 24-hour format
    }

    return {
        top: ((eventStart * 60 - FIRST_MIN) / MINUTES_IN_DAY) * CALENDAR_HEIGHT + HEADER_HEIGHT,
        height: (event.duration / MINUTES_IN_DAY) * CALENDAR_HEIGHT - 1, // 1 pixel between events
    };
};

// Convert decimal hour to formatted time string (e.g. "9 AM", "10:30 PM")
const formatHour = (decimalHour) => {
    const hours = Math.floor(decimalHour);
    const minutes = Math.round((decimalHour - hours) * 60);

    const isPM = hours >= 12 || hours < 9;
    const displayHours = hours % 12 === 0 ? 12 : hours % 12;

    if (minutes === 0) {
        return `${displayHours} ${isPM ? 'PM' : 'AM'}`;
    } else {
        const paddedMinutes = minutes.toString().padStart(2, '0');
        return `${displayHours}:${paddedMinutes} ${isPM ? 'PM' : 'AM'}`;
    }
};

// Return a string representing the formatted time of the event (e.g. "9 AM - 10:30 AM")
const getFormattedTime = event => {
    const eventStart = event.start;
    const eventEnd = eventStart + event.duration / 60;

    const startStr = formatHour(eventStart);
    const endStr = formatHour(eventEnd);

    return `${startStr} - ${endStr}`;
};

export const CalendarEvent = ({
    event,
    updateWeeks,
    dayLabel,
    weekIndex,
    weeks,
    allAssignments,
    cipAssignments
}) => {
    // The event position
    const eventPosition = getEventPosition(event);

    // The formatted time string
    const formattedTime = getFormattedTime(event);

    // Boolean for whether the event is being updated
    const [isEditing, setIsEditing] = useState(false);

    const handleUpdateEvent = (updatedEvent) => {
        const updatedWeeks = [...weeks];
        const week = updatedWeeks[weekIndex];
        const day = week.days.find(d => d.label === dayLabel);

        if (!day) return;

        // Remove the old event with same title
        day.events = day.events.filter(e => e.id !== event.id);

        // Add the updated event
        day.events.push(updatedEvent);

        // Update the week
        week.days = week.days.map(d =>
            d.label === dayLabel ? { ...d, events: day.events } : d,
        );
        updatedWeeks[weekIndex] = week;

        // Update the weeks state and in the database
        updateWeeks(updatedWeeks);
    }

    const handleDeleteEvent = () => {
        const updatedWeeks = [...weeks];
        const week = updatedWeeks[weekIndex];
        const day = week.days.find(d => d.label === dayLabel);

        if (!day) return;

        day.events = day.events.filter(e => e !== event);
        updateWeeks(updatedWeeks);
    };

    return (
        <>
            <EventWrapper
                color={EVENT_COLORS[event.type]}
                style={eventPosition}
                onClick={() => setIsEditing(true)}>
                <DeleteButton onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteEvent();
                }}>
                    X
                </DeleteButton>

                {(event.duration >= 20) && <EventTitle>{event.title}</EventTitle>}
                {(event.duration >= 60) &&
                    <div style={{ fontSize: '0.7rem', color: '#333' }}>{formattedTime}</div>}
            </EventWrapper>
            {isEditing && <EventModal
                mode="edit"
                initialData={event}
                onSubmit={handleUpdateEvent}
                onClose={() => setIsEditing(false)}
                allAssignments={allAssignments}
                cipAssignments={cipAssignments}
            />}
        </>
    );
};

const EventTitle = styled.div`
    font-size: 0.8rem;
`;

const EventWrapper = styled.div`
    display: flex;
    flex-direction: column;
    background: ${props => props.color};
    color: #000;
    font-size: 0.8rem;
    padding: 4px 6px;
    border-radius: 4px;
    position: absolute;
    left: 2px;
    right: 2px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
    overflow: hidden;
    textOverflow: 'ellipsis';
    white-space: nowrap;
    gap: 4px;
`;

const DeleteButton = styled.button`
    background: transparent;
    color: darkgray;
    border: none;
    cursor: pointer;
    border-radius: 4px;
    font-size: 0.6rem;
    font-weight: bold;
    position: absolute;
    right: 4px;
    width: 15px;
    height: 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2;

    &:hover {
        color: gray;
    }
`;
